import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft, Zap, Gift, Truck, Percent, Clock } from "lucide-react";
import api from "@/lib/api";
import { Icon } from "@/components/store/icons";
import { ProductRow } from "@/components/store/ProductRow";
import { ProductCard } from "@/components/store/ProductCard";
import { getRecent } from "@/lib/recentlyViewed";
import { useCurrency } from "@/context/CurrencyContext";

const SLIDES = [
  { t: "Mega Season Sale", s: "Up to 70% off across every category", c: "from-[#FF6A1A] to-[#E23744]", cta: "/shop?sale=true" },
  { t: "New Customer Deal", s: "Extra 10% off your first order with WELCOME10", c: "from-[#101A3D] to-[#2a3a7a]", cta: "/shop" },
  { t: "Tech Week", s: "Phones, laptops & gadgets at unbeatable prices", c: "from-[#0f766e] to-[#115e59]", cta: "/category/electronics" },
];

function Countdown() {
  const [t, setT] = useState(6 * 3600 + 42 * 60 + 11);
  useEffect(() => { const i = setInterval(() => setT((x) => (x > 0 ? x - 1 : 0)), 1000); return () => clearInterval(i); }, []);
  const h = String(Math.floor(t / 3600)).padStart(2, "0");
  const m = String(Math.floor((t % 3600) / 60)).padStart(2, "0");
  const s = String(t % 60).padStart(2, "0");
  return (
    <div className="flex items-center gap-1 font-mono">
      {[h, m, s].map((v, i) => <span key={i} className="bg-[#101A3D] text-white text-xs font-bold px-1.5 py-1 rounded">{v}</span>)}
    </div>
  );
}

export default function Home() {
  const [cats, setCats] = useState([]);
  const [home, setHome] = useState(null);
  const [slide, setSlide] = useState(0);
  const [recent] = useState(() => getRecent());
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/home").then((r) => setHome(r.data));
  }, []);
  useEffect(() => { const i = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 5000); return () => clearInterval(i); }, []);

  const flash = home?.flash_deals || [];

  return (
    <div className="bg-[#F1F3F6] min-h-screen">
      <div className="max-w-[1500px] mx-auto px-4 py-4 space-y-4">
        {/* HERO */}
        <div className="grid grid-cols-12 gap-4">
          <div className="hidden lg:block col-span-3 bg-white rounded-md border border-zinc-200 py-2">
            {cats.slice(0, 12).map((c) => (
              <Link key={c.key} to={`/category/${c.key}`} className="flex items-center justify-between px-4 py-[7px] text-sm text-zinc-700 hover:bg-[#FFF3E6] hover:text-[#FF6A1A] group" data-testid={`sidecat-${c.key}`}>
                <span className="flex items-center gap-2.5"><Icon name={c.icon} size={16} className="text-zinc-400 group-hover:text-[#FF6A1A]" />{c.name}</span>
                <ChevronRight size={14} className="text-zinc-300" />
              </Link>
            ))}
          </div>

          <div className="col-span-12 lg:col-span-6 relative rounded-md overflow-hidden min-h-[260px] lg:min-h-[340px]">
            {SLIDES.map((sl, i) => (
              <div key={i} className={`absolute inset-0 bg-gradient-to-br ${sl.c} flex flex-col justify-center px-8 transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                <p className="text-white/80 text-sm font-semibold uppercase tracking-widest">Qava Marketplace</p>
                <h2 className="text-white font-display text-3xl lg:text-5xl font-extrabold mt-2 leading-tight">{sl.t}</h2>
                <p className="text-white/90 mt-2 text-sm lg:text-base">{sl.s}</p>
                <Link to={sl.cta} className="mt-5 bg-white text-zinc-900 font-semibold text-sm px-6 py-2.5 rounded-full w-fit hover:bg-zinc-100">Shop Now</Link>
              </div>
            ))}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {SLIDES.map((_, i) => <button key={i} onClick={() => setSlide(i)} className={`w-2 h-2 rounded-full ${i === slide ? "bg-white" : "bg-white/40"}`} />)}
            </div>
            <button onClick={() => setSlide((slide - 1 + SLIDES.length) % SLIDES.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 rounded-full p-1"><ChevronLeft size={18} className="text-white" /></button>
            <button onClick={() => setSlide((slide + 1) % SLIDES.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 rounded-full p-1"><ChevronRight size={18} className="text-white" /></button>
          </div>

          <div className="col-span-12 lg:col-span-3 grid grid-cols-2 lg:grid-cols-1 gap-3">
            {[[Gift, "New Customer", "Extra 10% off", "bg-[#FFF3E6] text-[#FF6A1A]"], [Zap, "Flash Deals", "Ends tonight", "bg-[#FDECEE] text-[#E23744]"],
              [Truck, "Free Shipping", "Orders over $50", "bg-[#E8F5F0] text-emerald-600"], [Percent, "Daily Deals", "New every day", "bg-[#EEF1FB] text-[#101A3D]"]].map(([I, t, s, cl], i) => (
              <Link key={i} to="/shop?sale=true" className="bg-white rounded-md border border-zinc-200 p-3 flex items-center gap-3 hover:shadow-md transition-shadow">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cl}`}><I size={18} /></div>
                <div><p className="text-sm font-semibold text-zinc-800">{t}</p><p className="text-xs text-zinc-500">{s}</p></div>
              </Link>
            ))}
          </div>
        </div>

        {/* SHOP BY CATEGORY */}
        <section className="bg-white rounded-md border border-zinc-200 p-4">
          <h2 className="font-display text-lg font-bold mb-4">Shop by Category</h2>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
            {cats.map((c) => (
              <Link key={c.key} to={`/category/${c.key}`} className="flex flex-col items-center gap-2 group" data-testid={`cat-tile-${c.key}`}>
                <div className="w-full aspect-square rounded-full overflow-hidden border border-zinc-200 group-hover:border-[#FF6A1A] transition-colors">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                </div>
                <span className="text-[11px] text-center text-zinc-700 leading-tight line-clamp-2 group-hover:text-[#FF6A1A]">{c.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* FLASH DEALS */}
        {flash.length > 0 && (
          <section className="bg-white rounded-md border border-zinc-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#E23744] to-[#FF6A1A] text-white">
              <div className="flex items-center gap-3">
                <h2 className="font-display text-lg font-extrabold flex items-center gap-1.5"><Zap size={18} className="fill-white" />Flash Deals</h2>
                <span className="flex items-center gap-1.5 text-xs"><Clock size={13} />Ends in</span>
                <Countdown />
              </div>
              <Link to="/shop?sale=true" className="text-xs font-semibold bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full">See All Deals</Link>
            </div>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide p-3">
              {flash.map((p) => {
                const disc = p.compare_at_price > p.selling_price ? Math.round((1 - p.selling_price / p.compare_at_price) * 100) : 0;
                const pct = Math.min(95, Math.max(15, 100 - (p.sold_count % 80)));
                return (
                  <Link key={p.id} to={`/product/${p.slug}`} className="w-[150px] shrink-0 group" data-testid={`flash-${p.slug}`}>
                    <div className="aspect-square bg-zinc-50 rounded-md overflow-hidden relative">
                      <img src={(p.images || [])[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      {disc > 0 && <span className="absolute top-1 left-1 bg-[#E23744] text-white text-[11px] font-bold px-1.5 py-0.5 rounded">-{disc}%</span>}
                    </div>
                    <p className="text-[12px] line-clamp-1 mt-1.5 text-zinc-800">{p.title}</p>
                    <p className="text-[#E23744] font-bold text-sm">{format(p.selling_price)}</p>
                    <div className="h-2 bg-zinc-200 rounded-full mt-1 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#FF6A1A] to-[#E23744]" style={{ width: `${pct}%` }} /></div>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{pct}% claimed</p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {home && <>
          <ProductRow title="Recently Viewed" products={recent} />
          <ProductRow title="Recommended for You" products={home.recommended} link="/shop" />
          <ProductRow title="Best Sellers" products={home.best_sellers} link="/shop?sort=best_selling" />
          <ProductRow title="Trending Products" products={home.trending} link="/shop?sort=trending" />
          <ProductRow title="Electronics Deals" products={home.electronics_deals} link="/category/electronics" />
          <ProductRow title="New Arrivals" products={home.new_arrivals} link="/shop?sort=newest" />
          <ProductRow title="Fashion Deals" products={home.fashion_deals} link="/category/womens-fashion" />
          <ProductRow title="Top Rated Products" products={home.top_rated} link="/shop?sort=top_rated" accent />
          <ProductRow title="Home Essentials" products={home.home_essentials} link="/category/home-kitchen" />
        </>}

        {home && (
          <section className="bg-white rounded-md border border-zinc-200 p-4">
            <h2 className="font-display text-lg font-bold mb-4">Explore More Products</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {[...(home.best_sellers || []), ...(home.new_arrivals || [])].slice(0, 18).map((p, i) => <ProductCard key={p.id + "" + i} product={p} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
